import { DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpResponse } from '@angular/common/http';

import { LoginService } from './login.service';
import { UserService } from './user.service';

/**
 * sessionStorage key prefix the resolved customer logo is cached under. A non-empty
 * sentinel ({@link NO_CUSTOMER_LOGO}) is stored when the customer has no logo, so
 * a "no logo" result is remembered (falsy checks can't distinguish "" from
 * "not fetched") and we don't re-request on every navigation.
 *
 * The customer id is appended (see {@link customerLogoKey}) so the entry is scoped
 * to the tenant it was fetched for. `UserService.resetUserServiceState()` drops the
 * avatar key but not this one, so a same-tab re-auth as a different customer — the
 * legacy SSO handoff, which never calls `clearSession()` — would otherwise read the
 * previous tenant's logo straight out of the cache and skip the fetch entirely.
 */
const CUSTOMER_LOGO_KEY = 'customerLogoImage';

/** Per-tenant cache key for {@link CUSTOMER_LOGO_KEY}. */
const customerLogoKey = (customerId: string | number): string =>
  `${CUSTOMER_LOGO_KEY}:${customerId}`;

/** Cached marker for "customer has no logo" — mapped back to `null` on read. */
const NO_CUSTOMER_LOGO = '__none__';

/**
 * Longest the sidebar keeps showing the logo skeleton with NOTHING in flight before
 * giving up and falling back to the RegTrack/License wordmark.
 *
 * "Pending" means *we have not been able to ask yet* — which includes the server
 * render and the window before `customer_id` lands in session state. `/auth-redirect`
 * is a chrome-BEARING route (deliberately — see its spec) reached with no customer id,
 * so a shell that stalls there would otherwise shimmer forever. This bound turns that
 * into the pre-existing wordmark fallback instead of a permanent placeholder.
 *
 * It deliberately does NOT cut a request short (see {@link CustomerLogoService.armPendingTimeout}):
 * a slow network is exactly when pre-empting the fetch would paint the wordmark and then
 * swap the logo in on top — the flash this whole mechanism exists to remove. Because the
 * in-flight case is excluded, the bound can be short enough that a genuinely stuck shell
 * doesn't pulse for long.
 */
export const CUSTOMER_LOGO_PENDING_TIMEOUT_MS = 3000;

/**
 * Owns the sidebar customer-logo request lifecycle: the per-tenant sessionStorage cache,
 * in-flight tracking, stale-response rejection, the skeleton's pending state and its
 * timeout bound. Extracted from `AppComponent` so the root component only coordinates
 * shell/sidebar state (CLAUDE.md Important Rule 4 — keep components thin, put logic in
 * services; API calls live in services).
 *
 * **Provider scope: the consuming component's `providers`, NOT `providedIn: 'root'`.**
 * The state here is one shell's brand-resolution state, and it is deliberately
 * instance-scoped for the same reason the shared filter controls scope their signal
 * services (see CLAUDE.md *Per-instance signal service*): a root singleton is shared by
 * every `AppComponent` alive in an injector, so a second shell would inherit the first's
 * already-armed {@link pendingTimer} and never arm its own. In production exactly one
 * shell exists, so the scoping is a no-op there; it is what keeps each shell independent
 * under test.
 *
 * What is deliberately NOT here: the `afterNextRender` hydration gate. The component
 * decides *when* it is safe to ask (the client must hydrate the same `@if` branch the
 * server serialised); this service decides *how*.
 */
@Injectable()
export class CustomerLogoService {
  /** Fetches the customer logo (`/common/customer-image`) for {@link refresh}. */
  private readonly loginService = inject(LoginService);

  /** Source of the `customer_id` the logo is fetched for. Read on every refresh,
      because the shell is constructed before login populates it. */
  private readonly userService = inject(UserService);

  /** Gates the sessionStorage-backed logo cache to the browser — this is an SSR
      app and the server render has no sessionStorage. */
  private readonly platformId = inject(PLATFORM_ID);

  /** Component-scoped (see the class doc), so this is the host component's destroy
      scope: no timer outlives the shell, in tests or at its own destruction. */
  private readonly destroyRef = inject(DestroyRef);

  /** The customer id of the logo request currently in flight, or `null` when idle.
      Guards against a second request firing for the SAME tenant while one is in flight
      (rapid navigations can call {@link refresh} before the first caches) — and, because
      it records WHICH tenant asked rather than merely that someone did, it is also how a
      response that outlived its tenant is recognised and dropped instead of painting the
      previous tenant's logo into the current shell. */
  private requestCustomerId: string | number | null = null;

  /** Handle for the {@link CUSTOMER_LOGO_PENDING_TIMEOUT_MS} bound on the skeleton.
      Armed once, browser-only, on the first {@link refresh}; cleared the moment the
      logo settles. */
  private pendingTimer: ReturnType<typeof setTimeout> | null = null;

  /** The tenant whose brand {@link _logoUrl} currently holds, or `null` whenever that
      signal is `null` — the wordmark is nobody's brand, so it is never stale. This is the
      OUTBOUND half of the invariant {@link releaseRequest} guards on the inbound side: a
      late response must not paint a departed tenant's logo, and neither may a logo already
      on screen simply stay there once a different tenant is being fetched. Without it
      {@link refresh} has nothing to compare against — `requestCustomerId` says who is being
      asked about, not who is on display. */
  private logoCustomerId: string | number | null = null;

  private readonly _logoUrl = signal<string | null>(null);
  private readonly _pending = signal<boolean>(true);

  /** Customer logo shown in the sidebar (a `data:` URI), or `null` to fall back to
      the RegTrack/License wordmark. Resolved browser-only and cache-guarded; a
      signal so the OnPush view updates when the async fetch resolves. */
  readonly logoUrl = this._logoUrl.asReadonly();

  /**
   * True while the customer logo is still UNKNOWN — the sidebar renders a skeleton
   * placeholder instead of guessing a brand.
   *
   * Starts `true`, including on the server: the SSR/prerendered HTML is the very
   * first thing painted, and with a `null` {@link logoUrl} it used to bake in
   * the RegTrack wordmark, which hydration then swapped for the real customer logo —
   * the one-second flash on every refresh. Shipping the skeleton in that HTML instead
   * means the brand area only ever resolves once, to its final value.
   *
   * It flips to `false` ONLY when we have an answer: a cache hit, a settled fetch (logo
   * or an authoritative "no logo"), a fetch failure, or the
   * {@link CUSTOMER_LOGO_PENDING_TIMEOUT_MS} bound. It deliberately does NOT settle on
   * "no customer id yet" — that is "cannot ask", not "asked and got nothing", and
   * settling there would just move the same flash later, to the post-login navigation.
   *
   * CRITICALLY, it also does not settle until the first client render has happened, even
   * when the answer is sitting in sessionStorage and could be read synchronously in the
   * constructor. The client must hydrate the SAME `@if` branch the server serialised: pick
   * a different one and the server's nodes are never claimed, so they linger in the DOM —
   * visibly, behind the branch the client did render — until Angular's post-hydration
   * cleanup runs at application stability. Measured at ~1.5s on a real page. That is the
   * whole "logo flashes then changes" bug, and it is why the caller defers the first
   * {@link refresh} to `afterNextRender` rather than running it eagerly.
   */
  readonly pending = this._pending.asReadonly();

  constructor() {
    this.destroyRef.onDestroy(() => this.clearPendingTimeout());
  }

  /**
   * Resolves the sidebar's customer logo. Order of resolution:
   * 1. If a value is cached in sessionStorage under this customer's
   *    {@link customerLogoKey}, use it directly ({@link NO_CUSTOMER_LOGO} maps back
   *    to `null`) and skip the call.
   * 2. Otherwise fetch it from `/common/customer-image` for the current customer.
   *    On a `200` with a non-empty base64 string, normalise it (use a `data:` URI
   *    as-is, else prefix `data:image/png;base64,`) and cache it.
   * 3. On a success response carrying no image, cache the {@link NO_CUSTOMER_LOGO}
   *    sentinel — that is the server's authoritative "this tenant has no logo", so
   *    we don't re-request it on every navigation.
   * 4. On a transport/server error, fall back to the wordmark but cache NOTHING: a
   *    transient blip must not become a session-long "no logo". The next navigation
   *    retries.
   *
   * The response shape mirrors the profile-image endpoint (`response.body.result`
   * carries the base64 payload), the closest sibling `/common/*-image` call.
   *
   * Browser-only: it reads/writes sessionStorage (absent during the SSR pass), so
   * it no-ops on the server. No-ops until a customer is known (`customer_id` falsy),
   * so the pre-login shell doesn't cache a null over a real logo and permanently
   * short-circuit the post-login fetch.
   *
   * Every one of those outcomes also settles {@link pending} — except the
   * two "we could not ask" ones (server render, no customer id yet), which keep the
   * sidebar's skeleton up so no brand is ever painted and then replaced. See
   * {@link settle} / {@link armPendingTimeout}.
   *
   * This method is only the ORDER those steps run in, plus the guards that stop early;
   * the cache read, the request and the response normalisation are one helper each
   * ({@link applyCached}, {@link fetchLogo}, {@link toLogoUrl}). The sequence stays here
   * on purpose — every early `return` is a deliberate "do not settle", and which ones
   * settle and which ones do not is the whole contract of this class.
   * @returns {void}
   */
  refresh(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Stay pending: the server render must NOT emit the wordmark, or hydration
      // swaps it for the real logo and the user sees the flash this guards against.
      return;
    }

    // Armed before the customer-id guard on purpose — "no customer id yet" is the
    // very case that can leave the skeleton up indefinitely.
    this.armPendingTimeout();

    const customerId = this.userService.getCustomerId();
    if (!customerId) {
      return;
    }

    const cacheKey = customerLogoKey(customerId);
    if (this.applyCached(customerId, cacheKey)) {
      return;
    }

    // Dedupe only against a request for the SAME tenant. A customer change must be
    // allowed to start its own request even while the previous tenant's is still open,
    // or the shell keeps the old logo until some later navigation happens to retry.
    if (this.requestCustomerId === customerId) {
      return;
    }
    this.requestCustomerId = customerId;
    this.dropDepartedTenantLogo(customerId);
    this.fetchLogo(customerId, cacheKey);
  }

  /**
   * Returns the sidebar to its skeleton when the brand on screen answers for a DIFFERENT
   * tenant than the one now being fetched — the same-tab re-auth the per-tenant cache key
   * already guards against (the legacy SSO handoff never calls `clearSession()`).
   *
   * Without this, {@link pending} is already `false` from the previous tenant's answer, so
   * nothing re-enters the skeleton and the shell keeps showing tenant A's logo to tenant B
   * for the whole length of B's request. That is the very thing {@link releaseRequest}
   * refuses to do with a late RESPONSE, so it must not happen by omission either: a shell
   * that cannot answer "whose brand is this?" shows no brand.
   *
   * Deliberately conditional on the tenant having actually CHANGED. A re-request for the
   * SAME tenant (its sessionStorage entry cleared out from under us) must not flicker the
   * logo away and back — what is on screen is still the right answer while we re-ask.
   * A `null` {@link logoUrl} needs no reset either: the wordmark belongs to no tenant.
   *
   * @param customerId The tenant whose logo is about to be requested.
   * @returns {void}
   */
  private dropDepartedTenantLogo(customerId: string | number): void {
    if (this._logoUrl() === null || this.logoCustomerId === customerId) {
      return;
    }
    this._logoUrl.set(null);
    this.logoCustomerId = null;
    this._pending.set(true);
    // The skeleton this just re-entered needs its own bound, exactly like the first one:
    // `armPendingTimeout` stood down earlier in this same `refresh` because `pending` was
    // still false at the top of it.
    this.armPendingTimeout();
  }

  /**
   * Resolves the logo straight from this tenant's sessionStorage entry when there is one,
   * settling the skeleton without a request ({@link NO_CUSTOMER_LOGO} maps back to `null`).
   *
   * @param customerId The tenant being resolved — recorded as {@link logoCustomerId} so a
   *   later tenant switch can tell whose brand is on screen.
   * @param cacheKey This tenant's {@link customerLogoKey}.
   * @returns {boolean} True when the cache answered — the caller must then NOT fetch.
   */
  private applyCached(customerId: string | number, cacheKey: string): boolean {
    const cached = sessionStorage.getItem(cacheKey);
    if (!cached) {
      return false;
    }
    this._logoUrl.set(cached === NO_CUSTOMER_LOGO ? null : cached);
    this.logoCustomerId = customerId;
    this.settle();
    return true;
  }

  /**
   * Issues the `/common/customer-image` request for one tenant and settles the shell
   * whichever way it lands. The caller has already claimed {@link requestCustomerId}.
   *
   * `customerId` is a parameter, not a re-read, because it is the tenant this response
   * ANSWERS FOR — not necessarily the tenant on screen by the time it lands. Both handlers
   * compare the two before touching shell state; see {@link releaseRequest}.
   *
   * @param customerId The tenant this request is issued for.
   * @param cacheKey That tenant's {@link customerLogoKey}.
   * @returns {void}
   */
  private fetchLogo(customerId: string | number, cacheKey: string): void {
    this.loginService.getCustomerLogo({ customer_id: customerId }).subscribe({
      next: (response) => {
        const url = this.toLogoUrl(response);
        // Cached unconditionally: `cacheKey` is scoped to the tenant that asked, so a
        // late answer still memoises the right thing under the right key — a tenant
        // switched back to later reads it from cache instead of re-fetching.
        // Only reached for a 2xx, so an absent image here is an authoritative
        // "no logo" and is safe to memoise.
        sessionStorage.setItem(cacheKey, url ?? NO_CUSTOMER_LOGO);
        if (!this.releaseRequest(customerId)) {
          return;
        }
        this._logoUrl.set(url);
        this.logoCustomerId = url === null ? null : customerId;
        // Settles for BOTH outcomes above — a real logo and an authoritative
        // "no logo" are equally an answer.
        this.settle();
      },
      error: () => {
        // Deliberately no cache write: a 5xx/network failure is not evidence that
        // the tenant has no logo, and memoising the sentinel here would suppress
        // every retry for the rest of the session.
        if (!this.releaseRequest(customerId)) {
          return;
        }
        this._logoUrl.set(null);
        this.logoCustomerId = null;
        // We asked and failed — show the wordmark rather than shimmering on. A later
        // navigation still retries (nothing was cached) and can replace it.
        this.settle();
      },
    });
  }

  /**
   * Normalises a customer-image response into the `data:` URI the sidebar binds, or `null`
   * for "no image in this response". Only a `200` carrying a non-empty base64 payload is an
   * image; anything else maps to `null`, which the caller memoises as the authoritative
   * {@link NO_CUSTOMER_LOGO}.
   *
   * @param response The raw `/common/customer-image` response.
   * @returns {string | null} A `data:` URI, or `null` when the response carries no image.
   */
  private toLogoUrl(response: HttpResponse<{ result?: unknown }>): string | null {
    const base64 = response.body?.result as string | undefined;
    if (response.status !== 200 || !base64) {
      return null;
    }
    return base64.startsWith('data:image') ? base64 : `data:image/png;base64,${base64}`;
  }

  /**
   * Drops the pending-timeout so it can't fire after the fact. Idempotent. Public so a
   * caller that gives up (or tears down) earlier than this service's own destroy scope can
   * stand the timer down explicitly. The service also registers it on its own `DestroyRef`
   * which, being component-provided, IS the host component's destroy scope — so a host
   * does not need to re-register it.
   * @returns {void}
   */
  clearPendingTimeout(): void {
    if (this.pendingTimer !== null) {
      clearTimeout(this.pendingTimer);
      this.pendingTimer = null;
    }
  }

  /**
   * Ends a logo request's in-flight bookkeeping and reports whether its answer may still
   * drive the shell.
   *
   * Two independent reasons an answer is stale, and they need different handling:
   * - A NEWER request has since been issued, so it — not this one — owns
   *   {@link requestCustomerId} and will settle the shell itself. Leave the token
   *   alone; clearing it here would let a third request start while the newer one is open.
   * - The token still names this request, but the signed-in customer has moved on without
   *   a new request having been issued yet. Release the token (nothing is in flight any
   *   more, and a later refresh must be free to run — including one for THIS tenant if the
   *   user switches back), but do not touch shell state.
   *
   * Writing {@link logoUrl} in either case would paint the previous tenant's logo
   * into the current tenant's shell; settling {@link pending} would end the
   * skeleton on an answer to a question nobody is asking any more.
   * @param requestCustomerId The customer id the response answers for.
   * @returns {boolean} True when the response is still authoritative for the shell.
   */
  private releaseRequest(requestCustomerId: string | number): boolean {
    if (this.requestCustomerId !== requestCustomerId) {
      return false;
    }
    this.requestCustomerId = null;
    return this.userService.getCustomerId() === requestCustomerId;
  }

  /**
   * Ends the skeleton: the logo is now whatever {@link logoUrl} holds. Idempotent,
   * and drops the pending-timeout so it can't fire after the fact.
   * @returns {void}
   */
  private settle(): void {
    this._pending.set(false);
    this.clearPendingTimeout();
  }

  /**
   * Arms the one-shot {@link CUSTOMER_LOGO_PENDING_TIMEOUT_MS} bound on the skeleton.
   * Browser-only (the caller has already checked) and a no-op once the logo has settled.
   *
   * The callback stands down while a request is in flight: that request settles the
   * skeleton itself on either outcome, and cutting it short would show the wordmark and
   * then replace it with the logo seconds later — the flash this guards against, on
   * exactly the slow connections that suffer it most. Dropping the handle there lets the
   * next {@link refresh} re-arm, so a request that never settles at all still
   * gets a fresh bound on the following navigation.
   * @returns {void}
   */
  private armPendingTimeout(): void {
    if (this.pendingTimer !== null || !this._pending()) {
      return;
    }
    this.pendingTimer = setTimeout(() => {
      this.pendingTimer = null;
      if (this.requestCustomerId !== null) {
        return;
      }
      this.settle();
    }, CUSTOMER_LOGO_PENDING_TIMEOUT_MS);
  }
}
